import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./MosPatientenListe.module.css";
import MOSFragebogen from "../MOSFragebogen/MOSFragebogen";
import { useMOSFragebogen } from "../../context/MOSFragebogenContext";
const API = process.env.REACT_APP_API_URL;

function authHeaders() {
  try {
    const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    return { "Content-Type": "application/json", Authorization: `Bearer ${stored.jwt}` };
  } catch {
    return { "Content-Type": "application/json" };
  }
}

function SortTh({ col, label, sortBy, sortDir, onSort, className = "" }) {
  const active = sortBy === col;
  return (
    <th className={`${styles.sortTh} ${className}`} onClick={() => onSort(col)}>
      {label}
      <span className={styles.sortIcon}>{active ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}</span>
    </th>
  );
}

export default function MosPatientenListe() {
  const navigate = useNavigate();
  const { clearMOSFragebogen } = useMOSFragebogen();
  const [view, setView]         = useState("list"); // "list" | "new" | "view"
  const [selectedId, setSelectedId] = useState(null);
  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [perPage, setPerPage]   = useState(10);
  const [meta, setMeta]         = useState({ current_page: 1, last_page: 1, total: 0 });
  const [sortBy, setSortBy]     = useState("created_at");
  const [sortDir, setSortDir]   = useState("desc");
  const [search, setSearch]     = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [creatingLink, setCreatingLink] = useState(null);
  const [linkResult, setLinkResult]     = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, per_page: perPage, search: debouncedSearch, sort_by: sortBy, sort_dir: sortDir,
      });
      const res  = await fetch(`${API}/api/mos-fragebogen?${params}`, { headers: authHeaders() });
      const data = await res.json();
      setRecords(data.data ?? []);
      setMeta({ current_page: data.current_page ?? 1, last_page: data.last_page ?? 1, total: data.total ?? 0 });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortDir, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  // Reload whenever the user returns to the list from new/view
  const prevView = useRef(null);
  useEffect(() => {
    if (prevView.current !== null && prevView.current !== "list" && view === "list") {
      load();
    }
    prevView.current = view;
  }, [view, load]);

  const handleSort = (col) => {
    if (col === sortBy) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
    setPage(1);
  };

  const fmtDate = (val) => val ? new Date(val).toLocaleDateString("de-DE") : "–";

  const handleCreateLink = async (r) => {
    setCreatingLink(r.id);
    try {
      const res = await fetch(`${API}/api/mos-links`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ name: r.name, vorname: r.vorname, fragebogen_id: r.id }),
      });
      if (res.ok) setLinkResult(true);
    } finally {
      setCreatingLink(null);
    }
  };

  if (view === "new") {
    return (
      <MOSFragebogen
        onDone={() => setView("list")}
      />
    );
  }

  if (view === "view") {
    return (
      <MOSFragebogen
        readOnly={true}
        fragebogenId={selectedId}
        onDone={() => setView("list")}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.card}>

          <div className={styles.header}>
            <div>
              <h2 className={styles.title}>MOS Patienten</h2>
              <p className={styles.sub}>
                {meta.total > 0 ? `${meta.total} Einträge gesamt` : "Alle gespeicherten Fragebogen"}
              </p>
            </div>
            <button className={styles.addBtn} onClick={() => { clearMOSFragebogen(); setView("new"); }}>
              + Neuen Patienten
            </button>
            <button className={styles.refreshBtn} onClick={load} disabled={loading} title="Aktualisieren">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
          </div>

          <div className={styles.searchBar}>
            <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Nach Name oder Vorname suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className={styles.searchClear} onClick={() => setSearch("")}>✕</button>
            )}
          </div>

          <div className={styles.mobileSort}>
            <label className={styles.mobileSortLabel} htmlFor="mobileSort">Sortieren:</label>
            <select
              id="mobileSort"
              className={styles.mobileSortSelect}
              value={`${sortBy}:${sortDir}`}
              onChange={(e) => {
                const [col, dir] = e.target.value.split(":");
                setSortBy(col); setSortDir(dir); setPage(1);
              }}
            >
              <option value="created_at:desc">Erstellt am – neueste zuerst</option>
              <option value="created_at:asc">Erstellt am – älteste zuerst</option>
              <option value="patienten_id:asc">Patienten-ID – aufsteigend</option>
              <option value="patienten_id:desc">Patienten-ID – absteigend</option>
            </select>
          </div>

          {loading ? (
            <p className={styles.hint}>Wird geladen…</p>
          ) : records.length === 0 ? (
            <p className={styles.hint}>Keine Einträge gefunden.</p>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <SortTh col="patienten_id" label="Patienten-ID" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <th>Name</th>
                    <th>Vorname</th>
                    <th>Vorerhebung</th>
                    <th>Letzte Erfolgskontrolle</th>
                    <SortTh col="created_at" label="Erstellt am" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id}>
                      <td className={styles.idCell} data-label="Patienten-ID">{r.patienten_id}</td>
                      <td data-label="Name">{r.name || <span className={styles.empty}>–</span>}</td>
                      <td data-label="Vorname">{r.vorname || <span className={styles.empty}>–</span>}</td>
                      <td data-label="Vorerhebung">
                        {r.vorerhebung_at
                          ? <span className={styles.badgeDone}>✓ {fmtDate(r.vorerhebung_at)}</span>
                          : <span className={styles.badgePending}>–</span>}
                      </td>
                      <td data-label="Letzte Erfolgskontrolle">
                        {r.episodes_max_completed_at
                          ? <span className={styles.badgeDone}>
                              ✓ {fmtDate(r.episodes_max_completed_at)}
                              {(r.episodes_count ?? 0) > 1 && <> ({r.episodes_count}×)</>}
                            </span>
                          : <span className={styles.badgePending}>–</span>}
                      </td>
                      <td className={styles.dateCell} data-label="Erstellt am">{fmtDate(r.created_at)}</td>
                      <td className={styles.actionCell}>
                        <div className={styles.actionBtns}>
                          <button className={styles.viewBtn} onClick={() => { setSelectedId(r.id); setView("view"); }}>
                            Ansehen
                          </button>
                          <button
                            className={styles.linkBtn}
                            onClick={() => handleCreateLink(r)}
                            disabled={creatingLink === r.id}
                          >
                            {creatingLink === r.id ? "Wird erstellt…" : "Link erstellen"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={styles.pagination}>
            <div className={styles.perPageWrap}>
              <label className={styles.perPageLabel} htmlFor="perPage">Einträge:</label>
              <select id="perPage" className={styles.perPageSelect} value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className={styles.pageControls}>
              <button className={styles.pageBtn} onClick={() => setPage((p) => p - 1)} disabled={page === 1}>Zurück</button>
              <span className={styles.pageInfo}>Seite {meta.current_page} von {meta.last_page}</span>
              <button className={styles.pageBtn} onClick={() => setPage((p) => p + 1)} disabled={page >= meta.last_page}>Weiter ›</button>
            </div>
          </div>

        </div>

        <div className={styles.actionsRow}>
          <button type="button" className={styles.primaryBtn} onClick={() => navigate(-1)}>Zurück</button>
        </div>
      </div>

      {/* ── Link result overlay ── */}
      {linkResult && (
        <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) setLinkResult(null); }}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Link erstellt</h3>
            <p className={styles.modalPatient}>
              Der Link wurde erfolgreich erstellt und ist in der MOS-Links Tabelle einsehbar.
            </p>
            <div className={styles.modalActions}>
              <button className={styles.modalCloseBtn} onClick={() => setLinkResult(null)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
